import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Search,
  Plus,
  Trash2,
  Save,
  ExternalLink,
  FileText,
  Layers,
  ArrowLeftRight,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  X,
  Tag,
  Building2,
  Link2,
  BookOpen,
  Globe,
  Palette,
  Package,
  Upload,
  PlayCircle,
  DollarSign,
  LifeBuoy,
  Phone,
  Mail,
  User,
  Shield,
  Cable,
  Pencil,
  GripVertical,
  Calendar,
  Receipt,
  BarChart3,
  ChevronUp,
  Loader2,
  Sparkles,
  MapPin,
} from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import TechDatabaseProducts from './TechDatabaseProducts';
import TechDatabaseVendors from './TechDatabaseVendors';
import TechDatabaseProperties from './TechDatabaseProperties';
import TagInput from './TagInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TechDatabase({ token, role, isSuperAdmin, navigateToPlatformId, onNavigationHandled }) {
  const [dbTab, setDbTab] = useState('platforms');
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformDetail, setPlatformDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showNewPlatform, setShowNewPlatform] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCatForm, setNewCatForm] = useState({ id: '', name: '', color: '#2F6F8E', description: '' });

  // Data completeness
  const [completeness, setCompleteness] = useState(null);
  const [showCompleteness, setShowCompleteness] = useState(false);
  const [quickFixData, setQuickFixData] = useState(null); // {entity, field, fieldLabel, items}
  const [quickFixEditing, setQuickFixEditing] = useState(null); // {id, value}

  // Collapsed categories in platform list
  const [collapsedCategories, setCollapsedCategories] = useState(null); // null = initialize on first render
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  // Import state
  const [showImport, setShowImport] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Product detail state
  const [productDetail, setProductDetail] = useState(null);
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  const [productEditMode, setProductEditMode] = useState(false);
  const [productEditForm, setProductEditForm] = useState({});
  const [productVendorDetail, setProductVendorDetail] = useState(null);
  const [productContacts, setProductContacts] = useState([]);

  // Add new solution/vendor/assignment dialogs
  const [showNewSolution, setShowNewSolution] = useState(false);
  const [newSolutionForm, setNewSolutionForm] = useState({});
  const [showNewVendorInline, setShowNewVendorInline] = useState(false);
  const [newVendorForm, setNewVendorForm] = useState({ name: '', website: '', headquarters: '' });
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [assignProductSearch, setAssignProductSearch] = useState('');
  const [newAssignmentForm, setNewAssignmentForm] = useState({ property_id: '', vendor_name: '', product_id: '', layer: 'actual' });
  const [allVendors, setAllVendors] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [properties, setProperties] = useState([]);

  // Integration editing state
  const [showNewIntegration, setShowNewIntegration] = useState(false);
  const [newIntegrationForm, setNewIntegrationForm] = useState({ target_platform_id: '', direction: 'two-way', label: '' });

  // Deploy to Properties state
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [showAllAssignments, setShowAllAssignments] = useState(false);

  // Property-level integrations state (Tier 3)
  const [propIntegPropertyId, setPropIntegPropertyId] = useState('');
  const [propIntegList, setPropIntegList] = useState([]);
  const [propIntegTargetId, setPropIntegTargetId] = useState('');
  const [allProperties, setAllProperties] = useState([]);

  const fetchPropertyIntegrations = async (propertyId) => {
    if (!propertyId) { setPropIntegList([]); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/property-integrations/${propertyId}`, { headers });
      // Filter to show only integrations involving this product
      const all = res.data.integrations || [];
      const relevant = all.filter(pi => pi.from_product_id === productDetail?.id || pi.to_product_id === productDetail?.id);
      setPropIntegList(relevant);
    } catch { setPropIntegList([]); }
  };

  // Load all properties for the property integration selector
  useEffect(() => {
    if (productDetailOpen && allProperties.length === 0) {
      axios.get(`${BACKEND_URL}/api/properties?show_all=true`, { headers })
        .then(res => setAllProperties(Array.isArray(res.data) ? res.data : res.data.properties || []))
        .catch(() => {});
    }
  }, [productDetailOpen]); // eslint-disable-line
  const [deploySelectedProperties, setDeploySelectedProperties] = useState([]);
  const [deploying, setDeploying] = useState(false);

  // Category management
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [navigateToProductId, setNavigateToProductId] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', color: '#6366f1' });

  // Brands state
  const [brands, setBrands] = useState([]);

  // Product integration editor state
  const [showAddProductInteg, setShowAddProductInteg] = useState(false);
  const [productIntegForm, setProductIntegForm] = useState({ target_product_id: '', direction: 'two-way', status: 'possible', integration_type: 'api', label: '', notes: '' });
  const [allProductsForInteg, setAllProductsForInteg] = useState([]);

  // Contract/cost state
  const [productContracts, setProductContracts] = useState([]);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractForm, setContractForm] = useState({ start_date: '', end_date: '', renewal_date: '', auto_renew: false, annual_value: '', terms: '', notes: '' });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  const fetchPlatforms = useCallback(async () => {
    setLoading(true);
    try {
      const [platRes, catRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tech-database/platforms`, { headers }),
        axios.get(`${BACKEND_URL}/api/categories`, { headers }),
      ]);
      setPlatforms(platRes.data);
      setCategories(catRes.data);
    } catch (err) {
      toast.error('Failed to load tech database');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  // Fetch data completeness score
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tech-database/completeness`, { headers })
      .then(res => setCompleteness(res.data))
      .catch(() => {});
  }, [headers]); // eslint-disable-line

  const openQuickFix = async (entity, field, fieldLabel) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tech-database/incomplete`, { headers, params: { entity, field, limit: 100 } });
      setQuickFixData({ entity, field, fieldLabel, items: res.data.items || [] });
    } catch { setQuickFixData(null); }
  };

  const handleQuickFixNavigate = (item) => {
    // Open inline edit for the missing field
    setQuickFixEditing({ id: item.id, value: '' });
  };

  const saveQuickFixField = async (item) => {
    if (!quickFixEditing?.value?.trim() || !quickFixData) return;
    const entity = quickFixData.entity;
    const field = quickFixData.field;
    try {
      const endpoint = entity === 'products' ? `/api/admin/products/${item.id}` :
                       entity === 'vendors' ? `/api/admin/vendors/${item.id}` :
                       `/api/admin/platforms/${item.id}`;
      await axios.put(`${BACKEND_URL}${endpoint}`, { [field]: quickFixEditing.value.trim() }, { headers });
      toast.success(`Updated ${item.name}`);
      setQuickFixData(prev => prev ? { ...prev, items: prev.items.filter(i => i.id !== item.id) } : null);
      setQuickFixEditing(null);
      axios.get(`${BACKEND_URL}/api/tech-database/completeness`, { headers }).then(r => setCompleteness(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const [autoFilling, setAutoFilling] = useState(false);
  const runAutoFill = async () => {
    if (!quickFixData?.items?.length) return;
    setAutoFilling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/ai-autofill`, {
        entity: quickFixData.entity,
        field: quickFixData.field,
        item_ids: quickFixData.items.map(i => i.id),
      }, { headers });
      toast.success(`Auto-filled ${res.data.updated} ${quickFixData.entity}`);
      setQuickFixData(null);
      // Refresh completeness
      const cRes = await axios.get(`${BACKEND_URL}/api/tech-database/completeness`, { headers });
      setCompleteness(cRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-fill failed');
    } finally { setAutoFilling(false); }
  };

  // Fetch brands
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/brands`, { headers }).then(res => setBrands(res.data || [])).catch(() => {});
  }, [headers]);

  // Handle navigation from "Manage in Tech Database" on the map
  useEffect(() => {
    if (navigateToPlatformId && platforms.length > 0) {
      setDbTab('platforms');
      setSelectedPlatform(navigateToPlatformId);
      fetchDetail(navigateToPlatformId);
      // Auto-expand the category containing this platform
      const plat = platforms.find(p => p.id === navigateToPlatformId);
      if (plat) {
        setCollapsedCategories(prev => {
          const current = prev || {};
          return { ...current, [plat.category_id]: false };
        });
      }
      if (onNavigationHandled) onNavigationHandled();
    }
  }, [navigateToPlatformId, platforms]); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchDetail = useCallback(async (platformId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tech-database/platforms/${platformId}`, { headers });
      setPlatformDetail(res.data);
      setEditForm(res.data);
    } catch (err) {
      toast.error('Failed to load platform details');
    } finally {
      setDetailLoading(false);
    }
  }, [headers]);

  const selectPlatform = (p) => {
    setSelectedPlatform(p.id);
    setEditMode(false);
    setShowAllAssignments(false);
    fetchDetail(p.id);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/import/preview`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setImportLoading(false);
    }
  };

  const executeImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const rows = importPreview.preview_rows.map(r => ({
        product_name: r.product_name,
        vendor_name: r.vendor_name,
        platform_id: r.platform_id || '',
        website: r.website,
      }));
      const res = await axios.post(`${BACKEND_URL}/api/admin/import/execute`, {
        rows,
        create_missing_vendors: true,
      }, { headers });
      toast.success(`Import complete: ${res.data.products_created} products, ${res.data.vendors_created} vendors created, ${res.data.skipped} skipped`);
      setShowImport(false);
      setImportPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const openProductDetail = async (product) => {
    setProductDetailOpen(true);
    setProductEditMode(false);
    setProductDetail(null);
    setProductVendorDetail(null);
    setProductContacts([]);
    setProductContracts([]);
    try {
      const [res, contractsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products/${product.id}`, { headers }),
        axios.get(`${BACKEND_URL}/api/contracts?product_id=${product.id}`, { headers }),
      ]);
      setProductDetail(res.data);
      setProductEditForm(res.data);
      setProductContracts(contractsRes.data.contracts || []);
      // Fetch vendor detail + contacts if vendor exists
      if (res.data.vendor_id) {
        const [vendorRes, contactsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/vendors/${res.data.vendor_id}`, { headers }),
          axios.get(`${BACKEND_URL}/api/vendors/${res.data.vendor_id}/contacts`, { headers }),
        ]);
        setProductVendorDetail(vendorRes.data);
        setProductContacts(contactsRes.data.contacts || []);
      }
    } catch (err) {
      toast.error('Failed to load product details');
    }
  };

  const saveProductFromDetail = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/products/${productEditForm.id}`, {
        name: productEditForm.name,
        vendor_id: productEditForm.vendor_id || '',
        platform_id: (productEditForm.platform_ids || [productEditForm.platform_id])[0] || productEditForm.platform_id,
        platform_ids: productEditForm.platform_ids || [productEditForm.platform_id].filter(Boolean),
        description: productEditForm.description || '',
        website: productEditForm.website || '',
        documentation_url: productEditForm.documentation_url || '',
        demo_url: productEditForm.demo_url || '',
        pricing_url: productEditForm.pricing_url || '',
        support_url: productEditForm.support_url || '',
        key_features: productEditForm.key_features || '',
        notes: productEditForm.notes || '',
        status: productEditForm.status || 'In Evaluation',
        tags: productEditForm.tags || [],
      }, { headers });
      toast.success('Product updated');
      setProductEditMode(false);
      // Refresh product detail
      const res = await axios.get(`${BACKEND_URL}/api/products/${productEditForm.id}`, { headers });
      setProductDetail(res.data);
      setProductEditForm(res.data);
      // Refresh platform detail to reflect changes
      if (selectedPlatform) fetchDetail(selectedPlatform);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  // Slug auto-generation helper
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Fetch vendors/products for inline add dialogs
  const fetchVendorsAndProducts = async (platformId) => {
    try {
      const [vRes, pRes, prRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors?limit=2000`, { headers }),
        axios.get(`${BACKEND_URL}/api/products?platform_id=${platformId}&limit=500`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
      ]);
      setAllVendors(vRes.data.vendors || []);
      setAllProducts(pRes.data.products || []);
      setProperties(prRes.data || []);
    } catch { /* ignore */ }
  };

  const createNewSolution = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/products`, {
        name: newSolutionForm.name,
        vendor_id: newSolutionForm.vendor_id || '',
        platform_id: selectedPlatform,
        description: newSolutionForm.description || '',
        website: newSolutionForm.website || '',
        status: newSolutionForm.status || 'In Evaluation',
        tags: [],
      }, { headers });
      toast.success('Solution created');
      setShowNewSolution(false);
      setNewSolutionForm({});
      if (selectedPlatform) fetchDetail(selectedPlatform);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const createNewVendorInline = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/vendors`, {
        name: newVendorForm.name,
        website: newVendorForm.website || '',
        headquarters: newVendorForm.headquarters || '',
      }, { headers });
      toast.success('Vendor created');
      setShowNewVendorInline(false);
      setNewVendorForm({ name: '', website: '', headquarters: '' });
      // Refresh vendors list
      fetchVendorsAndProducts(selectedPlatform);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const createNewAssignment = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: newAssignmentForm.property_id,
        platform_type_id: selectedPlatform,
        vendor_name: newAssignmentForm.vendor_name || '',
        product_id: newAssignmentForm.product_id || '',
        layer: newAssignmentForm.layer || 'actual',
      }, { headers });
      toast.success('Assignment created');
      setShowNewAssignment(false);
      setNewAssignmentForm({ property_id: '', vendor_name: '', product_id: '', layer: 'actual' });
      if (selectedPlatform) fetchDetail(selectedPlatform);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const deployToProperties = async () => {
    if (!productDetail || deploySelectedProperties.length === 0) return;
    setDeploying(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/assignments/bulk`, {
        product_id: productDetail.id,
        platform_type_id: productDetail.platform_id,
        property_ids: deploySelectedProperties,
        vendor_name: productDetail.vendor?.name || '',
        layer: 'actual',
      }, { headers });
      toast.success(`Deployed to ${res.data.created} properties (${res.data.skipped} skipped)`);
      setShowDeployDialog(false);
      setDeploySelectedProperties([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deploy failed');
    } finally {
      setDeploying(false);
    }
  };

  const savePlatform = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/platforms/${editForm.id}`, {
        id: editForm.id,
        name: editForm.name || '',
        short: editForm.short || '',
        category_id: editForm.category_id || '',
        importance: editForm.importance || 1,
        description: editForm.description || '',
        website_url: editForm.website_url || '',
        documentation_url: editForm.documentation_url || '',
        vendor_url: editForm.vendor_url || '',
        tags: editForm.tags || [],
        notes: editForm.notes || '',
      }, { headers });
      toast.success('Platform updated');
      setEditMode(false);
      fetchDetail(editForm.id);
      fetchPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const createPlatform = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/platforms`, {
        id: editForm.id,
        name: editForm.name || '',
        short: editForm.short || '',
        category_id: editForm.category_id || '',
        importance: editForm.importance || 1,
        description: editForm.description || '',
        website_url: editForm.website_url || '',
        documentation_url: editForm.documentation_url || '',
        vendor_url: editForm.vendor_url || '',
        tags: editForm.tags || [],
        notes: editForm.notes || '',
      }, { headers });
      toast.success('Platform created');
      setShowNewPlatform(false);
      setEditForm({});
      fetchPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const deletePlatform = async (id) => {
    if (!window.confirm('Delete this platform and all its assignments/integrations?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/platforms/${id}`, { headers });
      toast.success('Platform deleted');
      setSelectedPlatform(null);
      setPlatformDetail(null);
      fetchPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const createCategory = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/categories`, newCatForm, { headers });
      toast.success('Category created');
      setShowNewCategory(false);
      setNewCatForm({ id: '', name: '', color: '#2F6F8E', description: '' });
      fetchPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    }
  };

  // Filter platforms
  const filtered = useMemo(() => {
    return platforms.filter(p => {
      const matchSearch = !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.short.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.tags || []).some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchCat = filterCategory === 'all' || p.category_id === filterCategory;
      return matchSearch && matchCat;
    });
  }, [platforms, searchQuery, filterCategory]);

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    // Include ALL categories if toggle is on
    if (showAllCategories) {
      categories.forEach(cat => {
        groups[cat.id] = { id: cat.id, name: cat.name, color: cat.color || '#9CA3AF', platforms: [] };
      });
    }
    filtered.forEach(p => {
      const catId = p.category_id || 'uncategorized';
      if (!groups[catId]) {
        groups[catId] = { id: catId, name: catId === 'uncategorized' ? 'Uncategorized' : (p.category_name || 'Uncategorized'), color: catId === 'uncategorized' ? '#9CA3AF' : p.category_color, platforms: [] };
      }
      groups[catId].platforms.push(p);
    });
    Object.values(groups).forEach(g => g.platforms.sort((a, b) => (a.short || a.name).localeCompare(b.short || b.name)));
    return Object.values(groups).sort((a, b) => {
      if (a.id === 'uncategorized') return 1;
      if (b.id === 'uncategorized') return -1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered, categories, showAllCategories]);

  const handleTagInput = (value) => {
    const tags = value.split(',').map(t => t.trim()).filter(Boolean);
    setEditForm(prev => ({ ...prev, tags }));
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="tech-database-page">
      {/* Top-level tabs */}
      <div className="px-3 md:px-5 pt-3 pb-0 border-b bg-card/50 shrink-0 flex items-center justify-between gap-2">
        <Tabs value={dbTab} onValueChange={setDbTab}>
          <TabsList className="h-9">
            <TabsTrigger value="platforms" className="text-xs gap-1.5" data-testid="db-tab-platforms">
              <Layers className="h-3.5 w-3.5" /> Platforms
            </TabsTrigger>
            <TabsTrigger value="products" className="text-xs gap-1.5" data-testid="db-tab-products">
              <Package className="h-3.5 w-3.5" /> Products
            </TabsTrigger>
            <TabsTrigger value="vendors" className="text-xs gap-1.5" data-testid="db-tab-vendors">
              <Building2 className="h-3.5 w-3.5" /> Vendors
            </TabsTrigger>
            <TabsTrigger value="properties" className="text-xs gap-1.5" data-testid="db-tab-properties">
              <MapPin className="h-3.5 w-3.5" /> Properties
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {isAdmin && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs mb-1"
            onClick={() => { setShowImport(true); setImportPreview(null); }}
            data-testid="import-spreadsheet-button"
          >
            <Upload className="h-3.5 w-3.5" /> Import
          </Button>
        )}
      </div>

      {/* Data Completeness Score */}
      {completeness && (
        <div className="px-3 md:px-5 border-b bg-muted/20 shrink-0">
          <button onClick={() => setShowCompleteness(v => !v)}
            className="w-full flex items-center gap-3 py-2 text-xs group" data-testid="completeness-toggle">
            <BarChart3 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="font-medium text-muted-foreground">Data Completeness</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden max-w-[200px]">
              <div className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${completeness.overall}%`,
                  backgroundColor: completeness.overall >= 80 ? '#16a34a' : completeness.overall >= 50 ? '#d97706' : '#dc2626'
                }} />
            </div>
            <span className="font-bold tabular-nums" style={{
              color: completeness.overall >= 80 ? '#16a34a' : completeness.overall >= 50 ? '#d97706' : '#dc2626'
            }}>{completeness.overall}%</span>
            <ChevronUp className={`h-3 w-3 text-muted-foreground transition-transform ${showCompleteness ? '' : 'rotate-180'}`} />
          </button>
          {showCompleteness && (
            <div className="pb-3 grid grid-cols-1 sm:grid-cols-3 gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
              {[
                { key: 'products', label: 'Products', icon: Package, fields: ['description', 'website', 'key_features', 'tags', 'vendor_id'],
                  fieldLabels: { description: 'Description', website: 'Website', key_features: 'Key Features', tags: 'Tags', vendor_id: 'Vendor' }},
                { key: 'vendors', label: 'Vendors', icon: Building2, fields: ['website', 'headquarters', 'description'],
                  fieldLabels: { website: 'Website', headquarters: 'HQ Location', description: 'Description' }},
                { key: 'platforms', label: 'Platforms', icon: Layers, fields: ['description', 'tags'],
                  fieldLabels: { description: 'Description', tags: 'Tags' }},
              ].map(sec => {
                const data = completeness[sec.key];
                if (!data) return null;
                const scoreColor = data.score >= 80 ? '#16a34a' : data.score >= 50 ? '#d97706' : '#dc2626';
                const Icon = sec.icon;
                return (
                  <div key={sec.key} className="border rounded-lg p-3 bg-card" data-testid={`completeness-${sec.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold flex-1">{sec.label}</span>
                      <span className="text-sm font-bold tabular-nums" style={{ color: scoreColor }}>{data.score}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-2">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${data.score}%`, backgroundColor: scoreColor }} />
                    </div>
                    <div className="text-[10px] text-muted-foreground space-y-0.5">
                      <div className="flex justify-between">
                        <span>Complete</span>
                        <span className="font-medium">{data.complete} / {data.total}</span>
                      </div>
                      {sec.fields.filter(f => (data.missing[f] || 0) > 0).map(f => (
                        <button key={f} className="flex justify-between w-full hover:bg-amber-50 dark:hover:bg-amber-950/20 rounded px-1 -mx-1 transition-colors"
                          onClick={(e) => { e.stopPropagation(); openQuickFix(sec.key, f, sec.fieldLabels[f]); }}
                          data-testid={`quickfix-${sec.key}-${f}`}>
                          <span className="flex items-center gap-1">Missing {sec.fieldLabels[f]} <ArrowRight className="h-2.5 w-2.5 text-amber-500" /></span>
                          <span className="font-medium text-amber-600">{data.missing[f]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab content */}
      {dbTab === 'platforms' ? (
        <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-3 md:px-5 py-3 border-b bg-card/50 flex items-center gap-2 md:gap-3 shrink-0 flex-wrap">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid="tech-db-search"
            className="pl-9 h-9"
            placeholder="Search platforms, tags, descriptions..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[220px] h-9" data-testid="tech-db-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer shrink-0">
          <input type="checkbox" checked={showAllCategories} onChange={e => setShowAllCategories(e.target.checked)} className="rounded h-3.5 w-3.5" />
          Show empty
        </label>
        {isSuperAdmin && (
          <button onClick={() => setShowCategoryManager(true)} className="text-[10px] text-primary hover:underline shrink-0" data-testid="manage-categories-btn">
            Manage
          </button>
        )}
        <Badge variant="secondary" className="font-mono text-xs">
          {filtered.length} of {platforms.length} platforms
        </Badge>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1.5 text-xs"
              onClick={() => { setShowNewCategory(true); }}>
              <Palette className="h-3.5 w-3.5" /> New Category
            </Button>
            <Button size="sm" className="gap-1.5 text-xs"
              onClick={() => {
                setShowNewPlatform(true);
                setEditForm({ id: '', name: '', short: '', category_id: categories[0]?.id || '', importance: 1, description: '', tags: [] });
              }}>
              <Plus className="h-3.5 w-3.5" /> New Platform
            </Button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Platform list */}
        <ScrollArea className={`${platformDetail && typeof window !== 'undefined' && window.innerWidth < 768 ? 'hidden' : ''} w-full md:w-[380px] md:border-r shrink-0 ${!platformDetail ? 'flex-1 md:flex-none' : ''}`}>
          <div className="p-3 space-y-1">
            {loading ? (
              <p className="text-sm text-muted-foreground p-4 text-center">Loading...</p>
            ) : grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No platforms match your search</p>
            ) : (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={async (event) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  const oldIdx = grouped.findIndex(g => g.id === active.id);
                  const newIdx = grouped.findIndex(g => g.id === over.id);
                  if (oldIdx === -1 || newIdx === -1) return;
                  const reordered = arrayMove(grouped, oldIdx, newIdx);
                  try {
                    await axios.put(`${BACKEND_URL}/api/admin/categories/reorder`, {
                      category_ids: reordered.map(g => g.id),
                    }, { headers });
                    fetchPlatforms();
                  } catch { toast.error('Failed to reorder'); }
                }}
              >
              <SortableContext items={grouped.map(g => g.id)} strategy={verticalListSortingStrategy}>
              {grouped.map(group => {
                // Initialize all collapsed by default
                const isCollapsed = collapsedCategories === null
                  ? !group.platforms.some(p => p.id === selectedPlatform) // expand if selected platform is in this group
                  : (collapsedCategories[group.id] ?? true);

                const toggleCollapse = () => {
                  setCollapsedCategories(prev => {
                    const current = prev || {};
                    // If initializing, set all to true except this one
                    if (prev === null) {
                      const init = {};
                      grouped.forEach(g => { init[g.id] = g.id !== group.id; });
                      return init;
                    }
                    return { ...current, [group.id]: !isCollapsed };
                  });
                };

                return (
                <SortableCategoryRow key={group.id} id={group.id} isAdmin={isAdmin}>
                    <button
                      onClick={toggleCollapse}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/40 transition-colors group/cat"
                    >
                      <ChevronDown className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                      {editingCategoryId === group.id ? (
                        <input
                          autoFocus
                          value={editingCategoryName}
                          onChange={e => setEditingCategoryName(e.target.value)}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter' && editingCategoryName.trim()) {
                              e.stopPropagation();
                              try {
                                await axios.put(`${BACKEND_URL}/api/admin/categories/${group.id}`, {
                                  id: group.id, name: editingCategoryName.trim(), color: group.color,
                                }, { headers });
                                toast.success('Category renamed');
                                setEditingCategoryId(null);
                                fetchPlatforms();
                              } catch { toast.error('Failed to rename'); }
                            } else if (e.key === 'Escape') {
                              setEditingCategoryId(null);
                            }
                          }}
                          onBlur={() => setEditingCategoryId(null)}
                          onClick={e => e.stopPropagation()}
                          className="text-xs font-semibold text-foreground uppercase tracking-wider flex-1 text-left bg-transparent border-b border-primary outline-none"
                        />
                      ) : (
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex-1 text-left">{group.name}</span>
                      )}
                      <Badge variant="secondary" className="text-[10px] px-1.5 h-4 font-mono">{group.platforms.length}</Badge>
                    </button>
                    {isAdmin && editingCategoryId !== group.id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingCategoryId(group.id); setEditingCategoryName(group.name); }}
                        className="opacity-0 group-hover/cat:opacity-100 transition-opacity p-1 rounded hover:bg-muted shrink-0"
                        title="Rename category"
                      >
                        <Pencil className="h-3 w-3 text-muted-foreground" />
                      </button>
                    )}
                  {!isCollapsed && (
                  <div className="space-y-0.5 mt-0.5">
                    {group.platforms.map(p => (
                      <button
                        key={p.id}
                        onClick={() => selectPlatform(p)}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                          selectedPlatform === p.id
                            ? 'bg-accent ring-1 ring-ring/20'
                            : 'hover:bg-muted/60'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p.name}</div>
                        </div>
                        <Badge variant="outline" className="text-[9px] shrink-0">
                          {p.property_count} pr
                        </Badge>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                  )}
                </SortableCategoryRow>
                );
              })}
              </SortableContext>
              </DndContext>
            )}
          </div>
        </ScrollArea>

        {/* Right: Platform detail */}
        <div className={`flex-1 overflow-auto min-w-0 ${!platformDetail && typeof window !== 'undefined' && window.innerWidth < 768 ? 'hidden' : ''}`}>
          {platformDetail && typeof window !== 'undefined' && window.innerWidth < 768 && (
            <button onClick={() => { setSelectedPlatform(null); setPlatformDetail(null); }} className="flex items-center gap-1 px-4 py-2 text-xs text-primary border-b w-full text-left bg-muted/20">
              ← Back to platforms
            </button>
          )}
          {!selectedPlatform ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <BookOpen className="h-10 w-10 text-muted-foreground/25 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Select a platform to view details</p>
              </div>
            </div>
          ) : detailLoading ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : platformDetail ? (
            <ScrollArea className="h-full">
              <div className="p-6 max-w-3xl space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge style={{ backgroundColor: `${platformDetail.category_color}18`, color: platformDetail.category_color, borderColor: `${platformDetail.category_color}30` }} variant="outline" className="text-xs">
                        {platformDetail.category_name}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        Importance: {platformDetail.importance || 1}
                      </Badge>
                    </div>
                    {editMode ? (
                      <div className="space-y-2 mt-2">
                        <Input value={editForm.name} onChange={e => setEditForm(prev => ({...prev, name: e.target.value}))} placeholder="Full name" />
                        <div className="flex gap-2">
                          <Input value={editForm.short} onChange={e => setEditForm(prev => ({...prev, short: e.target.value}))} placeholder="Short name" className="w-40" />
                          <Select value={editForm.category_id} onValueChange={v => setEditForm(prev => ({...prev, category_id: v}))}>
                            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={String(editForm.importance || 1)} onValueChange={v => setEditForm(prev => ({...prev, importance: parseInt(v)}))}>
                            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Low</SelectItem>
                              <SelectItem value="2">Medium</SelectItem>
                              <SelectItem value="3">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{platformDetail.name}</h2>
                        <p className="text-sm text-muted-foreground font-mono">{platformDetail.short} &middot; {platformDetail.id}</p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isAdmin && !editMode && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>Edit</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePlatform(platformDetail.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {editMode && (
                      <>
                        <Button size="sm" onClick={savePlatform} className="gap-1"><Save className="h-3.5 w-3.5" /> Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditMode(false); setEditForm(platformDetail); }}>Cancel</Button>
                      </>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Description */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Description</h3>
                  {editMode ? (
                    <Textarea
                      value={editForm.description || ''}
                      onChange={e => setEditForm(prev => ({...prev, description: e.target.value}))}
                      placeholder="Enter a description of this platform type, its purpose, common use cases..."
                      rows={4}
                    />
                  ) : (
                    <p className="text-sm text-foreground/80">{platformDetail.description || 'No description yet.'}</p>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tags</h3>
                  {editMode ? (
                    <TagInput
                      value={editForm.tags || []}
                      onChange={tags => setEditForm(prev => ({ ...prev, tags }))}
                      token={token}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(platformDetail.tags || []).length > 0 ? (
                        platformDetail.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="text-xs"><Tag className="h-3 w-3 mr-1" />{tag}</Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No tags.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notes</h3>
                  {editMode ? (
                    <Textarea
                      value={editForm.notes || ''}
                      onChange={e => setEditForm(prev => ({...prev, notes: e.target.value}))}
                      placeholder="Internal notes, caveats, migration plans..."
                      rows={3}
                    />
                  ) : (
                    <p className="text-sm text-foreground/80">{platformDetail.notes || 'No notes.'}</p>
                  )}
                </div>

                <Separator />

                {/* Products / Solutions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Products / Solutions ({platformDetail.products?.length || 0})
                    </h3>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => {
                        setNewSolutionForm({ name: '', vendor_id: '', description: '', website: '', status: 'In Evaluation' });
                        setShowNewSolution(true);
                        fetchVendorsAndProducts(platformDetail.id);
                      }} data-testid="add-new-solution">
                        <Plus className="h-3 w-3" /> Add Solution
                      </Button>
                    )}
                  </div>
                  {(platformDetail.products || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No products mapped to this platform.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {platformDetail.products.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => openProductDetail(p)}
                          className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/40 text-xs text-left hover:bg-muted/70 hover:ring-1 hover:ring-ring/20 transition-all cursor-pointer"
                          data-testid={`product-card-${p.id}`}
                        >
                          <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{p.name}</div>
                            {p.vendor_name && <div className="text-muted-foreground truncate">{p.vendor_name}</div>}
                          </div>
                          {p.deployment_count > 0 && (
                            <Badge variant="secondary" className="text-[10px] shrink-0 font-mono" data-testid={`deployment-count-${p.id}`}>
                              {p.deployment_count} {p.deployment_count === 1 ? 'deploy' : 'deploys'}
                            </Badge>
                          )}
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${
                            p.status === 'Deployed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            p.status === 'Vetted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            p.status === 'Retired' ? 'bg-slate-50 text-slate-500 border-slate-200' :
                            p.status === 'None' ? 'bg-gray-50 text-gray-400 border-gray-200' :
                            p.status === 'Not Approved' ? 'bg-red-50 text-red-600 border-red-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            {p.status || 'None'}
                          </Badge>
                          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Property Assignments */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Property Assignments ({platformDetail.assignments?.length || 0})
                    </h3>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => {
                        setNewAssignmentForm({ property_id: '', vendor_name: '', product_id: '', layer: 'actual' });
                        setShowNewAssignment(true);
                        fetchVendorsAndProducts(platformDetail.id);
                      }} data-testid="add-new-assignment">
                        <Plus className="h-3 w-3" /> Add Assignment
                      </Button>
                    )}
                  </div>
                  {(platformDetail.assignments || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No properties use this platform.</p>
                  ) : (() => {
                    const sorted = [...(platformDetail.assignments || [])].sort((a, b) => (a.property_name || '').localeCompare(b.property_name || ''));
                    // Group by tenant
                    const tenantGroups = {};
                    sorted.forEach(a => {
                      const tName = a.tenant_name || 'Unassigned';
                      if (!tenantGroups[tName]) tenantGroups[tName] = [];
                      tenantGroups[tName].push(a);
                    });
                    const LIMIT = 10;
                    const totalCount = sorted.length;
                    const visibleGroups = showAllAssignments ? tenantGroups : (() => {
                      const limited = {}; let count = 0;
                      for (const [t, items] of Object.entries(tenantGroups).sort((a, b) => a[0].localeCompare(b[0]))) {
                        if (count >= LIMIT) break;
                        const take = items.slice(0, LIMIT - count);
                        limited[t] = take;
                        count += take.length;
                      }
                      return limited;
                    })();
                    const hasMore = totalCount > LIMIT && !showAllAssignments;
                    return (
                    <div className="space-y-3">
                      {Object.entries(visibleGroups).sort((a, b) => a[0].localeCompare(b[0])).map(([tName, items]) => (
                        <div key={tName}>
                          {Object.keys(tenantGroups).length > 1 && (
                            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{tName} ({tenantGroups[tName].length})</div>
                          )}
                          <div className="grid grid-cols-2 gap-1.5">
                            {items.map((a, i) => (
                              <div key={a.id || i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 text-xs group">
                                <Building2 className="h-3 w-3 text-muted-foreground shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate text-[11px]">{a.property_name}</div>
                                  <div className="text-muted-foreground truncate text-[10px]">{a.product_name || a.vendor_name}</div>
                                </div>
                                {isAdmin && a.id && (
                                  <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0"
                                    onClick={async () => {
                                      if (!window.confirm(`Remove ${a.property_name} from ${platformDetail.name}?`)) return;
                                      try {
                                        await axios.delete(`${BACKEND_URL}/api/admin/assignments/${a.id}`, { headers });
                                        toast.success('Assignment removed');
                                        fetchDetail(selectedPlatform);
                                      } catch { toast.error('Failed'); }
                                    }} data-testid={`delete-assign-${a.id}`}>
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {hasMore && (
                        <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowAllAssignments(true)} data-testid="show-all-assignments">
                          Show All ({totalCount - LIMIT} more)
                        </Button>
                      )}
                      {showAllAssignments && totalCount > LIMIT && (
                        <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowAllAssignments(false)}>
                          Show Less
                        </Button>
                      )}
                    </div>
                    );
                  })()}
                </div>

                {/* Integrations */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Integrations ({platformDetail.integrations?.length || 0})
                    </h3>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => {
                        setNewIntegrationForm({ target_platform_id: '', direction: 'two-way', label: '' });
                        setShowNewIntegration(true);
                      }} data-testid="add-new-integration">
                        <Plus className="h-3 w-3" /> Add Integration
                      </Button>
                    )}
                  </div>
                  {(platformDetail.integrations || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No integrations defined.</p>
                  ) : (() => {
                    // Group by category, alpha within each
                    const catGroups = {};
                    platformDetail.integrations.forEach(integ => {
                      const cat = integ.category_name || 'Other';
                      if (!catGroups[cat]) catGroups[cat] = [];
                      catGroups[cat].push(integ);
                    });
                    Object.values(catGroups).forEach(arr => arr.sort((a, b) => {
                      const aName = a.from_platform_id === platformDetail.id ? a.to_platform_name : a.from_platform_name;
                      const bName = b.from_platform_id === platformDetail.id ? b.to_platform_name : b.from_platform_name;
                      return (aName || '').localeCompare(bName || '');
                    }));
                    return (
                    <div className="space-y-3">
                      {Object.entries(catGroups).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, integs]) => (
                        <div key={cat}>
                          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: integs[0]?.category_color || '#888' }} />
                            {cat} ({integs.length})
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {integs.map((integ, i) => {
                              const isSource = integ.from_platform_id === platformDetail.id;
                              const otherName = isSource ? integ.to_platform_name : integ.from_platform_name;
                              const dirLabel = integ.direction === 'two-way' ? '↔' : (isSource ? '→' : '←');
                              return (
                                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 text-xs group">
                                  <span className="font-mono text-sm shrink-0 w-4 text-center text-muted-foreground">{dirLabel}</span>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-[11px]">{otherName}</span>
                                    {integ.label && <span className="text-muted-foreground ml-1 text-[10px]">— {integ.label}</span>}
                                  </div>
                                  {isAdmin && (
                                    <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0"
                                      onClick={async () => {
                                        if (!window.confirm(`Delete integration with ${otherName}?`)) return;
                                        try {
                                          await axios.delete(`${BACKEND_URL}/api/admin/integrations`, {
                                            headers,
                                            params: { from_platform_id: integ.from_platform_id, to_platform_id: integ.to_platform_id },
                                          });
                                          toast.success('Integration deleted');
                                          fetchDetail(platformDetail.id);
                                        } catch { toast.error('Failed to delete'); }
                                      }}
                                      data-testid={`delete-integration-${integ.from_platform_id}-${integ.to_platform_id}`}
                                    >
                                      <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              </div>
            </ScrollArea>
          ) : null}
        </div>
      </div>
      </div>
      ) : dbTab === 'products' ? (
        <TechDatabaseProducts token={token} role={role} isSuperAdmin={isSuperAdmin} navigateToProductId={navigateToProductId} onProductNavigated={() => setNavigateToProductId(null)} />
      ) : dbTab === 'vendors' ? (
        <TechDatabaseVendors token={token} role={role} isSuperAdmin={isSuperAdmin} />
      ) : dbTab === 'properties' ? (
        <TechDatabaseProperties token={token} role={role} />
      ) : null}

      {/* New Platform Dialog */}
      <Dialog open={showNewPlatform} onOpenChange={setShowNewPlatform}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Platform Type</DialogTitle>
            <DialogDescription>Add a new technology platform to the database</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Full Name</Label>
              <Input value={editForm.name || ''} onChange={e => {
                const name = e.target.value;
                setEditForm(prev => ({...prev, name, id: generateSlug(name), short: prev.short || ''}));
              }} placeholder="Customer Relationship Management" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Short Name</Label>
                <Input value={editForm.short || ''} onChange={e => setEditForm(prev => ({...prev, short: e.target.value}))} placeholder="CRM" data-testid="new-platform-short" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={editForm.category_id || ''} onValueChange={v => setEditForm(prev => ({...prev, category_id: v}))}>
                  <SelectTrigger data-testid="new-platform-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Importance</Label>
                <Select value={String(editForm.importance || 1)} onValueChange={v => setEditForm(prev => ({...prev, importance: parseInt(v)}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Low (1)</SelectItem>
                    <SelectItem value="2">Medium (2)</SelectItem>
                    <SelectItem value="3">High (3)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={editForm.description || ''} onChange={e => setEditForm(prev => ({...prev, description: e.target.value}))} placeholder="Platform description..." rows={3} />
            </div>
            <div>
              <Label className="text-xs">Tags</Label>
              <TagInput
                value={editForm.tags || []}
                onChange={tags => setEditForm(prev => ({ ...prev, tags }))}
                token={token}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewPlatform(false)}>Cancel</Button>
            <Button onClick={createPlatform} disabled={!editForm.id || !editForm.name || !editForm.category_id}>Create Platform</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Fix Panel */}
      <Dialog open={!!quickFixData} onOpenChange={() => setQuickFixData(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <BarChart3 className="h-4 w-4 text-amber-500" />
              Quick Fix: Missing {quickFixData?.fieldLabel}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {quickFixData?.items?.length || 0} {quickFixData?.entity} missing this field. Click any item to navigate to it.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 max-h-[55vh]">
            <div className="space-y-1 p-1">
              {(quickFixData?.items || []).map(item => (
                <div key={item.id} className="rounded-lg border text-xs overflow-hidden" data-testid={`quickfix-item-${item.id}`}>
                  <button onClick={() => handleQuickFixNavigate(item)}
                    className="w-full text-left p-2.5 hover:bg-muted/60 transition-colors group">
                    <div className="flex items-center gap-2">
                      <span className="font-medium flex-1 truncate">{item.name}</span>
                      {item.platform_name && <Badge variant="outline" className="text-[8px] shrink-0">{item.platform_name}</Badge>}
                      {item.vendor_name && <span className="text-[10px] text-muted-foreground shrink-0">{item.vendor_name}</span>}
                      {item.category_id && <Badge variant="outline" className="text-[8px] shrink-0">{item.category_id}</Badge>}
                      <ChevronUp className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${quickFixEditing?.id === item.id ? '' : 'rotate-180'}`} />
                    </div>
                  </button>
                  {quickFixEditing?.id === item.id && (
                    <div className="px-2.5 pb-2.5 space-y-1.5 bg-muted/20 border-t">
                      <label className="text-[10px] font-medium text-muted-foreground block pt-1.5">
                        {quickFixData.fieldLabel} for {item.name}
                      </label>
                      <Input
                        value={quickFixEditing.value}
                        onChange={e => setQuickFixEditing(prev => ({ ...prev, value: e.target.value }))}
                        placeholder={`Enter ${quickFixData.fieldLabel?.toLowerCase()}...`}
                        className="h-8 text-xs"
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && saveQuickFixField(item)}
                        data-testid={`quickfix-edit-input-${item.id}`}
                      />
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="ghost" className="flex-1 text-xs h-7" onClick={() => setQuickFixEditing(null)}>Cancel</Button>
                        <Button size="sm" className="flex-1 text-xs h-7 gap-1" onClick={() => saveQuickFixField(item)} disabled={!quickFixEditing.value.trim()}
                          data-testid={`quickfix-save-${item.id}`}>
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {(quickFixData?.items || []).length === 0 && (
                <div className="text-center py-8">
                  <BarChart3 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">All items have this field filled in!</p>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter className="flex-row gap-2">
            <Button variant="ghost" size="sm" onClick={() => setQuickFixData(null)}>Close</Button>
            {isAdmin && (quickFixData?.items?.length || 0) > 0 && (
              <Button size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white" onClick={runAutoFill} disabled={autoFilling}
                data-testid="autofill-btn">
                {autoFilling ? <><Loader2 className="h-3 w-3 animate-spin" /> Enriching...</> : <><Sparkles className="h-3 w-3" /> AI Auto-Fill All ({quickFixData?.items?.length})</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Category Dialog */}
      <Dialog open={showNewCategory} onOpenChange={setShowNewCategory}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Category</DialogTitle>
            <DialogDescription>Add a new technology category/group</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={newCatForm.name} onChange={e => {
                const name = e.target.value;
                setNewCatForm(prev => ({...prev, name, id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}));
              }} placeholder="AI & Automation Tools" data-testid="new-category-name" />
            </div>
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex gap-2">
                <input type="color" value={newCatForm.color} onChange={e => setNewCatForm(prev => ({...prev, color: e.target.value}))} className="h-9 w-12 rounded border cursor-pointer" />
                <Input value={newCatForm.color} onChange={e => setNewCatForm(prev => ({...prev, color: e.target.value}))} className="flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={newCatForm.description} onChange={e => setNewCatForm(prev => ({...prev, description: e.target.value}))} placeholder="Category description..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewCategory(false)}>Cancel</Button>
            <Button onClick={createCategory} disabled={!newCatForm.id || !newCatForm.name}>Create Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Spreadsheet Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4" /> Import Spreadsheet
            </DialogTitle>
            <DialogDescription>Upload an .xlsx file to import products and vendors into the catalogue.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-4 py-2">
            {!importPreview ? (
              <div className="border-2 border-dashed rounded-xl p-8 text-center">
                {importLoading ? (
                  <div>
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Parsing spreadsheet...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">Drop an .xlsx file or click to browse</p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      id="import-file-input"
                      onChange={handleFileUpload}
                      data-testid="import-file-input"
                    />
                    <Button variant="outline" onClick={() => document.getElementById('import-file-input')?.click()}>
                      Choose File
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{importPreview.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {importPreview.total_rows} rows · Columns: {importPreview.columns.join(', ')}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setImportPreview(null)}>Change File</Button>
                </div>
                <Separator />
                <div className="flex gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    Matched: {importPreview.preview_rows.filter(r => r.platform_matched).length}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    Unmatched platform: {importPreview.preview_rows.filter(r => !r.platform_matched).length}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    New vendors: {importPreview.preview_rows.filter(r => !r.vendor_matched && r.vendor_name).length}
                  </div>
                </div>
                <ScrollArea className="h-[300px] border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card border-b">
                      <tr>
                        <th className="text-left p-2 font-medium">Product</th>
                        <th className="text-left p-2 font-medium">Vendor</th>
                        <th className="text-left p-2 font-medium">Platform</th>
                        <th className="text-left p-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview_rows.map((row, i) => (
                        <tr key={i} className="border-b hover:bg-muted/30">
                          <td className="p-2">{row.product_name || '-'}</td>
                          <td className="p-2">
                            {row.vendor_name || '-'}
                            {row.vendor_matched && <Badge className="ml-1 text-[9px] bg-emerald-100 text-emerald-700">exists</Badge>}
                          </td>
                          <td className="p-2">
                            {row.platform_name || '-'}
                            {row.platform_matched ? (
                              <Badge className="ml-1 text-[9px] bg-emerald-100 text-emerald-700">matched</Badge>
                            ) : (
                              <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700">unmatched</Badge>
                            )}
                          </td>
                          <td className="p-2">
                            {row.platform_matched ? (
                              <span className="text-emerald-600">Ready</span>
                            ) : (
                              <span className="text-amber-600">Needs mapping</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </>
            )}
          </div>
          {importPreview && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowImport(false)}>Cancel</Button>
              <Button
                onClick={executeImport}
                disabled={importing || importPreview.preview_rows.filter(r => r.platform_matched).length === 0}
                className="gap-1.5"
                data-testid="execute-import-button"
              >
                {importing ? 'Importing...' : `Import ${importPreview.preview_rows.filter(r => r.platform_matched).length} rows`}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Detail Dialog - Read Only Summary */}
      <Dialog open={productDetailOpen} onOpenChange={(open) => { if (!open) { setProductDetailOpen(false); setProductEditMode(false); } }}>
        <DialogContent className="max-w-lg" data-testid="product-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              <Package className="h-4 w-4 shrink-0" />
              {productDetail ? productDetail.name : 'Loading...'}
            </DialogTitle>
            {productDetail && (
              <DialogDescription className="flex items-center gap-2">
                {productDetail.platform?.name || productDetail.platform_id}
                {productDetail.vendor?.name && <> · <Building2 className="h-3 w-3 inline" /> {productDetail.vendor.name}</>}
              </DialogDescription>
            )}
          </DialogHeader>

          {!productDetail ? (
            <div className="py-12 text-center">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Status badges */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="text-xs">{productDetail.status || 'None'}</Badge>
                {productDetail.is_cloud !== false && <Badge variant="outline" className="text-[9px] text-sky-600 border-sky-200">Cloud</Badge>}
                {productDetail.is_cloud === false && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-200">On-Premise</Badge>}
                {productDetail.is_white_label && <Badge variant="outline" className="text-[9px] text-orange-600 border-orange-200">White-label</Badge>}
                {(productDetail.brand_designations || []).map(bd => (
                  <Badge key={bd.brand_id} className="text-[9px]" style={{ backgroundColor: `${bd.brand_color}18`, color: bd.brand_color, borderColor: `${bd.brand_color}40` }} variant="outline">
                    <Shield className="h-2.5 w-2.5 mr-0.5" />{bd.brand_name}
                  </Badge>
                ))}
              </div>

              {/* Description */}
              {productDetail.description && (
                <div>
                  <h4 className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Description</h4>
                  <p className="text-sm text-foreground/80">{productDetail.description}</p>
                </div>
              )}

              {/* Key info grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                {productDetail.website && (
                  <a href={productDetail.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-blue-600 hover:underline truncate">
                    <Globe className="h-3 w-3 shrink-0" /> {productDetail.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                  </a>
                )}
                {productDetail.key_features && (
                  <div className="col-span-2">
                    <h4 className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Key Features</h4>
                    <p className="text-xs text-foreground/70 whitespace-pre-wrap">{productDetail.key_features}</p>
                  </div>
                )}
              </div>

              {/* Tags */}
              {(productDetail.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {productDetail.tags.map((t, i) => (
                    <Badge key={i} variant="secondary" className="text-[9px]">{t}</Badge>
                  ))}
                </div>
              )}

              {/* Deployment count */}
              <div className="text-xs text-muted-foreground">
                Deployed at {productDetail.deployment_count || 0} properties
                {(productDetail.product_integrations || []).length > 0 && <> · {productDetail.product_integrations.length} integrations</>}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDetailOpen(false)}>Close</Button>
            <Button className="gap-1.5" onClick={() => {
              setProductDetailOpen(false);
              setNavigateToProductId(productDetail?.id);
              setDbTab('products');
            }} data-testid="view-full-product">
              <ExternalLink className="h-3.5 w-3.5" /> View Full Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Solution Dialog */}
      <Dialog open={showNewSolution} onOpenChange={setShowNewSolution}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Solution</DialogTitle>
            <DialogDescription>Add a product/solution to {platformDetail?.name || 'this platform'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Solution Name</Label>
              <Input value={newSolutionForm.name || ''} onChange={e => setNewSolutionForm(p => ({ ...p, name: e.target.value }))} placeholder="Product name" data-testid="new-solution-name" />
            </div>
            <div>
              <Label className="text-xs">Vendor</Label>
              <div className="flex gap-2">
                <Select value={newSolutionForm.vendor_id || 'none'} onValueChange={v => setNewSolutionForm(p => ({ ...p, vendor_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vendor</SelectItem>
                    {allVendors.slice(0, 100).map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="shrink-0 text-xs gap-1" onClick={() => setShowNewVendorInline(true)} data-testid="add-vendor-inline">
                  <Plus className="h-3 w-3" /> New
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={newSolutionForm.status || 'In Evaluation'} onValueChange={v => setNewSolutionForm(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['None', 'In Evaluation', 'Vetted', 'Deployed', 'Retired', 'Not Approved'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={newSolutionForm.website || ''} onChange={e => setNewSolutionForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={newSolutionForm.description || ''} onChange={e => setNewSolutionForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Brief description..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewSolution(false)}>Cancel</Button>
            <Button onClick={createNewSolution} disabled={!newSolutionForm.name} data-testid="save-new-solution">Create Solution</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Vendor Inline Dialog */}
      <Dialog open={showNewVendorInline} onOpenChange={setShowNewVendorInline}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Quick Add Vendor</DialogTitle>
            <DialogDescription>Create a new vendor company</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Vendor Name</Label>
              <Input value={newVendorForm.name} onChange={e => setNewVendorForm(p => ({ ...p, name: e.target.value }))} placeholder="Company name" data-testid="new-vendor-name" />
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={newVendorForm.website} onChange={e => setNewVendorForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">Headquarters</Label>
              <Input value={newVendorForm.headquarters} onChange={e => setNewVendorForm(p => ({ ...p, headquarters: e.target.value }))} placeholder="City, State" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewVendorInline(false)}>Cancel</Button>
            <Button onClick={createNewVendorInline} disabled={!newVendorForm.name} data-testid="save-new-vendor-inline">Create Vendor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Assignment Dialog */}
      <Dialog open={showNewAssignment} onOpenChange={setShowNewAssignment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Property Assignment</DialogTitle>
            <DialogDescription>Assign {platformDetail?.name || 'this platform'} to a property</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Property</Label>
              <Select value={newAssignmentForm.property_id || ''} onValueChange={v => setNewAssignmentForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger data-testid="assignment-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {(platformDetail?.assignments || []).length > 0 ? (
                    // Filter out already-assigned properties
                    properties.filter(pr => !platformDetail.assignments.some(a => a.property_id === pr.id && a.layer === newAssignmentForm.layer)).map(pr => (
                      <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                    ))
                  ) : (
                    properties.map(pr => (
                      <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Product / Solution</Label>
              <div className="relative">
                <Input
                  value={assignProductSearch}
                  onChange={e => { setAssignProductSearch(e.target.value); }}
                  placeholder="Search products..."
                  className="pr-8"
                  data-testid="assignment-product-search"
                />
                {assignProductSearch && (
                  <button onClick={() => { setAssignProductSearch(''); setNewAssignmentForm(p => ({ ...p, product_id: '', vendor_name: '' })); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {newAssignmentForm.product_id && (
                <div className="mt-1 text-xs text-emerald-600 font-medium">
                  Selected: {allProducts.find(p => p.id === newAssignmentForm.product_id)?.name || newAssignmentForm.product_id}
                </div>
              )}
              {assignProductSearch && !newAssignmentForm.product_id && (
                <div className="mt-1 border rounded-lg max-h-[200px] overflow-y-auto bg-card shadow-sm">
                  {allProducts
                    .filter(p => p.name.toLowerCase().includes(assignProductSearch.toLowerCase()) || (p.vendor_name || '').toLowerCase().includes(assignProductSearch.toLowerCase()))
                    .slice(0, 50)
                    .map(p => (
                      <button key={p.id} onClick={() => {
                        setNewAssignmentForm(prev => ({ ...prev, product_id: p.id, vendor_name: p.vendor_name || prev.vendor_name }));
                        setAssignProductSearch(p.name);
                      }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b last:border-0 flex items-center gap-2">
                        <span className="font-medium flex-1 truncate">{p.name}</span>
                        {p.vendor_name && <span className="text-muted-foreground shrink-0">{p.vendor_name}</span>}
                      </button>
                    ))}
                  {allProducts.filter(p => p.name.toLowerCase().includes(assignProductSearch.toLowerCase())).length === 0 && (
                    <p className="text-xs text-muted-foreground p-3 text-center">No products found</p>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vendor Name</Label>
                <Input value={newAssignmentForm.vendor_name} onChange={e => setNewAssignmentForm(p => ({ ...p, vendor_name: e.target.value }))} placeholder="Vendor" />
              </div>
              <div>
                <Label className="text-xs">Layer</Label>
                <Select value={newAssignmentForm.layer} onValueChange={v => setNewAssignmentForm(p => ({ ...p, layer: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actual">Actual</SelectItem>
                    <SelectItem value="possible">Possible</SelectItem>
                    <SelectItem value="ideal">Ideal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewAssignment(false)}>Cancel</Button>
            <Button onClick={createNewAssignment} disabled={!newAssignmentForm.property_id} data-testid="save-new-assignment">Add Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Integration Dialog */}
      <Dialog open={showNewIntegration} onOpenChange={setShowNewIntegration}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Integration</DialogTitle>
            <DialogDescription>Create an integration from {platformDetail?.short || 'this platform'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Other Platform</Label>
              <Select value={newIntegrationForm.target_platform_id || ''} onValueChange={v => setNewIntegrationForm(p => ({ ...p, target_platform_id: v }))}>
                <SelectTrigger data-testid="integration-target-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent className="max-h-[350px]">
                  {(() => {
                    const byCategory = {};
                    platforms.filter(p => p.id !== platformDetail?.id).forEach(p => {
                      const catName = p.category_name || 'Uncategorized';
                      const catColor = p.category_color || '#888';
                      if (!byCategory[catName]) byCategory[catName] = { color: catColor, items: [] };
                      byCategory[catName].items.push(p);
                    });
                    return Object.entries(byCategory).sort((a, b) => a[0].localeCompare(b[0])).map(([catName, cat]) => (
                      <SelectGroup key={catName}>
                        <SelectLabel className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                          {catName}
                        </SelectLabel>
                        {cat.items.sort((a, b) => (a.name || a.short).localeCompare(b.name || b.short)).map(p => (
                          <SelectItem key={p.id} value={p.id} className="pl-6">{p.name || p.short}</SelectItem>
                        ))}
                      </SelectGroup>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Direction</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { value: 'two-way', label: `${platformDetail?.short || 'This'} ↔ Other`, desc: 'Bidirectional' },
                  { value: 'one-way', label: `${platformDetail?.short || 'This'} → Other`, desc: 'This sends data' },
                  { value: 'one-way-inbound', label: `${platformDetail?.short || 'This'} ← Other`, desc: 'This receives data' },
                ].map(d => (
                  <button key={d.value} type="button" onClick={() => setNewIntegrationForm(p => ({ ...p, direction: d.value }))}
                    className={`p-2.5 rounded-lg border text-center text-xs transition-colors ${newIntegrationForm.direction === d.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/60'}`}>
                    <div className="font-semibold text-sm">{d.label.split(' ')[1]}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{d.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Data Types (optional)</Label>
              <Input value={newIntegrationForm.label} onChange={e => setNewIntegrationForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Guest Data, Rates, Reservations" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewIntegration(false)}>Cancel</Button>
            <Button
              disabled={!newIntegrationForm.target_platform_id}
              data-testid="save-new-integration"
              onClick={async () => {
                try {
                  const dir = newIntegrationForm.direction;
                  const isInbound = dir === 'one-way-inbound';
                  await axios.post(`${BACKEND_URL}/api/admin/integrations`, {
                    from_platform_id: isInbound ? newIntegrationForm.target_platform_id : platformDetail.id,
                    to_platform_id: isInbound ? platformDetail.id : newIntegrationForm.target_platform_id,
                    direction: dir === 'one-way-inbound' ? 'one-way' : dir,
                    label: newIntegrationForm.label || '',
                  }, { headers });
                  toast.success('Integration created');
                  setShowNewIntegration(false);
                  fetchDetail(platformDetail.id);
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
              }}
            >
              Create Integration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deploy to Properties Dialog */}
      <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Deploy to Properties</DialogTitle>
            <DialogDescription>
              Assign {productDetail?.name || 'this product'} to multiple properties at once.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-1 py-2">
              {properties.map(p => {
                const isSelected = deploySelectedProperties.includes(p.id);
                return (
                  <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-muted/40'}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        setDeploySelectedProperties(prev =>
                          isSelected ? prev.filter(id => id !== p.id) : [...prev, p.id]
                        );
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{p.name}</span>
                  </label>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{deploySelectedProperties.length} selected</span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowDeployDialog(false)}>Cancel</Button>
              <Button onClick={deployToProperties} disabled={deploying || deploySelectedProperties.length === 0} data-testid="execute-deploy">
                {deploying ? 'Deploying...' : `Deploy to ${deploySelectedProperties.length} Properties`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Integration Dialog */}
      <Dialog open={showAddProductInteg} onOpenChange={setShowAddProductInteg}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cable className="h-4 w-4" /> Add Product Integration
            </DialogTitle>
            <DialogDescription>Define an integration between {productDetail?.name || 'this product'} and another product.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Target Product</Label>
              <Select value={productIntegForm.target_product_id || ''} onValueChange={v => setProductIntegForm(p => ({ ...p, target_product_id: v }))}>
                <SelectTrigger data-testid="product-integ-target"><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>
                  {allProductsForInteg.slice(0, 100).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.platform_name ? ` (${p.platform_name})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Direction</Label>
                <Select value={productIntegForm.direction} onValueChange={v => setProductIntegForm(p => ({ ...p, direction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="two-way">Two-way</SelectItem>
                    <SelectItem value="one-way">One-way</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={productIntegForm.status} onValueChange={v => setProductIntegForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (live)</SelectItem>
                    <SelectItem value="possible">Possible (supported)</SelectItem>
                    <SelectItem value="missing">Missing (needed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Integration Type</Label>
              <Select value={productIntegForm.integration_type || 'api'} onValueChange={v => setProductIntegForm(p => ({ ...p, integration_type: v }))}>
                <SelectTrigger data-testid="product-integ-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="api">API / Direct Integration</SelectItem>
                  <SelectItem value="data_export">Data Export / SFTP / File Drop</SelectItem>
                  <SelectItem value="manual">Manual / Workaround</SelectItem>
                  <SelectItem value="hybrid">Hybrid (API + Data Export)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {productIntegForm.integration_type === 'data_export' ? 'File-based transfer: SFTP, email, CSV export, report drop' :
                 productIntegForm.integration_type === 'manual' ? 'Manual process: copy/paste, re-entry, or workaround' :
                 productIntegForm.integration_type === 'hybrid' ? 'Mix of API calls and file-based data transfer' :
                 'Direct system-to-system connection via API'}
              </p>
            </div>
            <div>
              <Label className="text-xs">Label (optional)</Label>
              <Input value={productIntegForm.label} onChange={e => setProductIntegForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Guest Data, Work Orders" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={productIntegForm.notes || ''} onChange={e => setProductIntegForm(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. Runs nightly at 2am via SFTP" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddProductInteg(false)}>Cancel</Button>
            <Button
              disabled={!productIntegForm.target_product_id}
              data-testid="save-product-integration"
              onClick={async () => {
                try {
                  await axios.post(`${BACKEND_URL}/api/admin/product-integrations`, {
                    from_product_id: productDetail.id,
                    to_product_id: productIntegForm.target_product_id,
                    direction: productIntegForm.direction,
                    status: productIntegForm.status,
                    integration_type: productIntegForm.integration_type || 'api',
                    label: productIntegForm.label || '',
                    notes: productIntegForm.notes || '',
                  }, { headers });
                  toast.success('Product integration created');
                  setShowAddProductInteg(false);
                  openProductDetail(productDetail);
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }}
            >
              Create Integration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Form Dialog */}
      <Dialog open={showContractForm} onOpenChange={setShowContractForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="h-4 w-4" /> Add Contract</DialogTitle>
            <DialogDescription>Track a contract for {productDetail?.name || 'this product'}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Property (leave empty for global/corporate contract)</Label>
              <Select value={contractForm.property_id || 'global'} onValueChange={v => setContractForm(p => ({ ...p, property_id: v === 'global' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Global" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (all properties)</SelectItem>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input type="date" value={contractForm.start_date} onChange={e => setContractForm(p => ({ ...p, start_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input type="date" value={contractForm.end_date} onChange={e => setContractForm(p => ({ ...p, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Renewal Date</Label>
                <Input type="date" value={contractForm.renewal_date} onChange={e => setContractForm(p => ({ ...p, renewal_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Annual Value ($)</Label>
                <Input type="number" value={contractForm.annual_value} onChange={e => setContractForm(p => ({ ...p, annual_value: e.target.value }))} placeholder="0" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={contractForm.auto_renew || false} onChange={e => setContractForm(p => ({ ...p, auto_renew: e.target.checked }))} className="rounded" />
              <Label className="text-xs">Auto-renew</Label>
            </div>
            <div>
              <Label className="text-xs">Terms / Notes</Label>
              <Textarea value={contractForm.terms || ''} onChange={e => setContractForm(p => ({ ...p, terms: e.target.value }))} rows={2} placeholder="Contract terms, SLA details..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowContractForm(false)}>Cancel</Button>
            <Button data-testid="save-contract" onClick={async () => {
              try {
                await axios.post(`${BACKEND_URL}/api/admin/contracts`, {
                  product_id: productDetail.id,
                  property_id: contractForm.property_id || '',
                  vendor_id: productDetail.vendor_id || '',
                  start_date: contractForm.start_date || '',
                  end_date: contractForm.end_date || '',
                  renewal_date: contractForm.renewal_date || '',
                  auto_renew: contractForm.auto_renew || false,
                  annual_value: parseFloat(contractForm.annual_value) || 0,
                  terms: contractForm.terms || '',
                  notes: contractForm.notes || '',
                }, { headers });
                toast.success('Contract saved');
                setShowContractForm(false);
                const res = await axios.get(`${BACKEND_URL}/api/contracts?product_id=${productDetail.id}`, { headers });
                setProductContracts(res.data.contracts || []);
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
            }}>
              Save Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Manager Dialog */}
      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage Categories & Platforms</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {categories.map(cat => {
              const catPlatforms = platforms.filter(p => p.category_id === cat.id).sort((a, b) => (a.name || a.short).localeCompare(b.name || b.short));
              return (
              <div key={cat.id} className="rounded-lg border overflow-hidden">
                {/* Category header */}
                <div className="flex items-center gap-2 p-2.5 bg-muted/30 group">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat.color || '#888' }} />
                  {editCat?.id === cat.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <Input value={catForm.name} onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))} className="h-7 text-xs flex-1" />
                      <input type="color" value={catForm.color} onChange={e => setCatForm(p => ({ ...p, color: e.target.value }))} className="w-7 h-7 rounded cursor-pointer" />
                      <Button size="sm" className="h-7 text-xs" onClick={async () => {
                        try {
                          await axios.put(`${BACKEND_URL}/api/admin/categories/${cat.id}`, catForm, { headers });
                          toast.success('Category updated');
                          setEditCat(null);
                          fetchPlatforms();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditCat(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-medium flex-1">{cat.name}</span>
                      <Badge variant="secondary" className="text-[9px] font-mono">{catPlatforms.length}</Badge>
                      <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => { setEditCat(cat); setCatForm({ name: cat.name, color: cat.color || '#888' }); }}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={async () => {
                        if (catPlatforms.length > 0) { toast.error(`Can't delete — ${catPlatforms.length} platforms use this category`); return; }
                        if (!window.confirm(`Delete category "${cat.name}"?`)) return;
                        try {
                          await axios.delete(`${BACKEND_URL}/api/admin/categories/${cat.id}`, { headers });
                          toast.success('Category deleted');
                          fetchPlatforms();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      }}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
                {/* Platforms in this category */}
                {catPlatforms.length > 0 && (
                  <div className="px-3 py-1.5 space-y-0.5 bg-card">
                    {catPlatforms.map(plat => (
                      <div key={plat.id} className="flex items-center gap-2 py-1 text-xs group/plat">
                        <Package className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{plat.name || plat.short}</span>
                        <Select defaultValue={cat.id} onValueChange={async (newCatId) => {
                          if (newCatId === cat.id) return;
                          try {
                            await axios.put(`${BACKEND_URL}/api/admin/platforms/${plat.id}`, { category_id: newCatId }, { headers });
                            toast.success(`Moved "${plat.name || plat.short}" to ${categories.find(c => c.id === newCatId)?.name}`);
                            fetchPlatforms();
                          } catch (e) { toast.error('Failed to move'); }
                        }}>
                          <SelectTrigger className="h-6 w-36 text-[10px] opacity-0 group-hover/plat:opacity-100 transition-opacity">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(c => (
                              <SelectItem key={c.id} value={c.id} className="text-xs">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || '#888' }} />
                                  {c.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryManager(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SortableCategoryRow({ id, children, isAdmin }) {
  const { attributes, setNodeRef, transform, transition, isDragging, listeners } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!isAdmin) {
    return <div>{children}</div>;
  }

  // Render with drag handle only on the grip icon
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-1 group/cat">
        <div {...listeners} className="cursor-grab touch-none p-1 -ml-1">
          <GripVertical className="h-3 w-3 text-muted-foreground/40" />
        </div>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}
